//# sourceMappingURL=random.mjs.map
/// <reference types="./random.d.mts" />
import * as $bit_array from "../../gleam_stdlib/gleam/bit_array.mjs";
import * as $bool from "../../gleam_stdlib/gleam/bool.mjs";
import * as $dict from "../../gleam_stdlib/gleam/dict.mjs";
import * as $float from "../../gleam_stdlib/gleam/float.mjs";
import * as $int from "../../gleam_stdlib/gleam/int.mjs";
import * as $list from "../../gleam_stdlib/gleam/list.mjs";
import * as $set from "../../gleam_stdlib/gleam/set.mjs";
import * as $string from "../../gleam_stdlib/gleam/string.mjs";
import {
  Ok,
  Error,
  toList,
  Empty as $Empty,
  prepend as listPrepend,
  CustomType as $CustomType,
  makeError,
  divideFloat,
} from "../gleam.mjs";
import { new_seed, random_int, random_float } from "../prng_ffi.mjs";

export { new_seed };

const FILEPATH = "src/prng/random.gleam";

class Generator extends $CustomType {
  constructor(step) {
    super();
    this.step = step;
  }
}

const min_positive = 2.2250738585072014e-308;

/**
 * The underlying algorith will work best for integers in the inclusive range
 * going from `min_int` up to `max_int`.
 *
 * It can generate values outside of that range, but they are "not as random".
 */
export const min_int = -2_147_483_648;

/**
 * The underlying algorith will work best for integers in the inclusive range
 * going from `min_int` up to `max_int`.
 *
 * It can generate values outside of that range, but they are "not as random".
 */
export const max_int = 2_147_483_647;

/**
 * Steps a `Generator(a)` producing a random value of type `a` using the given
 * seed as the source of randomness.
 *
 * The stepping logic is completely deterministic. This means that, given a
 * seed and a generator, you'll always get the same result.
 *
 * This is why this function also returns a new seed that can be used to make
 * subsequent calls to `step` to get other random values.
 *
 * Stepping a generator by hand can be quite cumbersome, so I recommend you
 * try [`sample`](#sample) instead.
 *
 * ## Examples
 *
 * ```gleam
 * let initial_seed = random.new_seed(11)
 * let dice_roll = random.int(1, 6)
 * let #(first_roll, new_seed) = random.step(dice_roll, initial_seed)
 * let #(second_roll, _) = random.step(dice_roll, new_seed)
 *
 * #(first_roll, second_roll)
 * // -> #(3, 2)
 * ```
 */
export function step(generator, seed) {
  return generator.step(seed);
}

/**
 * Generates integers in the given inclusive range.
 *
 * ## Examples
 *
 * Say you want to model the outcome of a dice, you could use `int` like this:
 *
 * ```gleam
 * let dice_roll = random.int(1, 6)
 * ```
 */
export function int(from, to) {
  let $ = from <= to;
  if ($) {
    return new Generator(
      (_capture) => { return random_int(_capture, from, to); },
    );
  } else {
    return new Generator(
      (_capture) => { return random_int(_capture, to, from); },
    );
  }
}

/**
 * Generates floating point numbers in the given inclusive range.
 *
 * ## Examples
 *
 * ```gleam
 * let probability = random.float(0.0, 1.0)
 * ```
 */
export function float(from, to) {
  let $ = from <= to;
  if ($) {
    return new Generator(
      (_capture) => { return random_float(_capture, from, to); },
    );
  } else {
    return new Generator(
      (_capture) => { return random_float(_capture, to, from); },
    );
  }
}

/**
 * Always generates the given value, no matter the seed used.
 *
 * ## Examples
 *
 * ```gleam
 * let always_eleven = random.constant(11)
 * random.random_sample(always_eleven)
 * // -> 11
 * ```
 */
export function constant(value) {
  return new Generator((seed) => { return [value, seed]; });
}

function do_fixed_size_list(loop$acc, loop$seed, loop$generator, loop$length) {
  while (true) {
    let acc = loop$acc;
    let seed = loop$seed;
    let generator = loop$generator;
    let length = loop$length;
    let $ = length <= 0;
    if ($) {
      return [acc, seed];
    } else {
      let $1 = step(generator, seed);
      let value = $1[0];
      let seed$1 = $1[1];
      loop$acc = listPrepend(value, acc);
      loop$seed = seed$1;
      loop$generator = generator;
      loop$length = length - 1;
    }
  }
}

/**
 * Generates a lists of a fixed size; its values are generated using the
 * given generator.
 *
 * ## Examples
 *
 * Imagine you're modelling a game of
 * [Risk](https://en.wikipedia.org/wiki/Risk_(game)); when a player "attacks"
 * they can roll three dice. You may model that outcome using `fixed_size_list`
 * like this:
 *
 * ```gleam
 * let dice_roll = random.int(1, 6)
 * let attack_outcome = random.fixed_size_list(dice_roll, 3)
 * ```
 */
export function fixed_size_list(generator, length) {
  return new Generator(
    (_capture) => {
      return do_fixed_size_list(toList([]), _capture, generator, length);
    },
  );
}

/**
 * Transforms a generator into another one based on its generated values.
 *
 * The random value generated by the given generator is fed into the `do`
 * function and the returned generator is used as the new generator.
 *
 * ## Examples
 *
 * `then` is a really powerful function, almost all functions exposed by this
 * library could be defined in term of it!
 * Take as an example `map`, it can be implemented like this:
 *
 * ```gleam
 * fn map(generator: Generator(a), with fun: fn(a) -> b) -> Generator(b) {
 *   random.then(generator, fn(value) {
 *     random.constant(fun(value))
 *   })
 * }
 * ```
 *
 * Notice how the `do` function needs to return a `Generator(b)`, you can
 * achieve that by wrapping any constant value with the `random.constant`
 * generator.
 *
 * > Code written with `then` can gain a lot in readability if you use the
 * > `use` syntax, especially if it has some deep nesting. As an example, this
 * > is how you can rewrite the previous example taking advantage of `use`:
 * >
 * > ```gleam
 * > fn map(generator: Generator(a), with fun: fn(a) -> b) -> Generator(b) {
 * >   use value <- random.then(generator)
 * >   random.constant(fun(value))
 * > }
 * > ```
 */
export function then$(generator, generator_from) {
  return new Generator(
    (seed) => {
      let $ = step(generator, seed);
      let value = $[0];
      let seed$1 = $[1];
      return step(generator_from(value), seed$1);
    },
  );
}

/**
 * Generates a list with a random size with at most 32 items.
 * Each item is generated using the given generator.
 *
 * This is similar to `fixed_size_list` with the difference that the size
 * is chosen randomly.
 */
export function list(generator) {
  return then$(
    int(0, 32),
    (_capture) => { return fixed_size_list(generator, _capture); },
  );
}

function get_by_weight(loop$first, loop$others, loop$countdown) {
  while (true) {
    let first = loop$first;
    let others = loop$others;
    let countdown = loop$countdown;
    let weight = first[0];
    let value = first[1];
    if (others instanceof $Empty) {
      return value;
    } else {
      let second = others.head;
      let rest = others.tail;
      let positive_weight = $float.absolute_value(weight);
      let $ = countdown > positive_weight;
      if ($) {
        loop$first = second;
        loop$others = rest;
        loop$countdown = countdown - positive_weight;
      } else {
        return value;
      }
    }
  }
}

/**
 * Transforms the values produced by a generator using the given function.
 *
 * ## Examples
 *
 * Imagine you want to make a generator for boolean values that returns
 * `True` and `False` with the same probability. You could do that using `map`
 * like this:
 *
 * ```gleam
 * let bool_generator = random.int(1, 2) |> random.map(fn(n) { n == 1 })
 * ```
 *
 * Here `map` allows you to transform the values produced by the initial
 * integer generator - either 1 or 2 - into boolean values: when the original
 * generator produces a 1, `bool_generator` will produce `True`; when the
 * original generator produces a 2, `bool_generator` will produce `False`.
 */
export function map(generator, fun) {
  return new Generator(
    (seed) => {
      let $ = step(generator, seed);
      let value = $[0];
      let seed$1 = $[1];
      return [fun(value), seed$1];
    },
  );
}

function sum_absolute_values(loop$list, loop$acc) {
  while (true) {
    let list = loop$list;
    let acc = loop$acc;
    if (list instanceof $Empty) {
      return acc;
    } else {
      let rest = list.tail;
      let value = list.head[0];
      loop$list = rest;
      loop$acc = acc + $float.absolute_value(value);
    }
  }
}

/**
 * Generates values from the given ones with a weighted probability.
 *
 * This generator can guarantee to produce values since it always takes at
 * least one item (as its first argument); if it were to accept just a list of
 * options, it could be called like this:
 *
 * ```gleam
 * weighted([])
 * ```
 *
 * In which case it would be impossible to actually produce any value: none was
 * provided!
 *
 * ## Examples
 *
 * Given the following type to model the outcome of a coin flip:
 *
 * ```gleam
 * pub type CoinFlip {
 *   Heads
 *   Tails
 * }
 * ```
 *
 * You could write a generator for a loaded coin that lands on head 75% of the
 * times like this:
 *
 * ```gleam
 * let loaded_coin = random.weighted(#(0.75, Heads), [#(0.25, Tails)])
 * ```
 *
 * In this example the weights add up to 1, but you could use any number: the
 * weights get added up to a `total` and the probability of each option is its
 * `weight` / `total`.
 */
export function weighted(first, others) {
  let total = sum_absolute_values(others, $float.absolute_value(first[0]));
  return map(
    float(0.0, total),
    (_capture) => { return get_by_weight(first, others, _capture); },
  );
}

/**
 * Generates values from the given ones with an equal probability.
 *
 * This generator can guarantee to produce values since it always takes at
 * least one item (as its first argument); if it were to accept just a list of
 * options, it could be called like this:
 *
 * ```gleam
 * uniform([])
 * ```
 *
 * In which case it would be impossible to actually produce any value: none was
 * provided!
 *
 * ## Examples
 *
 * Given the following type to model colors:
 *
 * ```gleam
 * pub type Color {
 *   Red
 *   Green
 *   Blue
 * }
 * ```
 *
 * You could write a generator that returns each color with an equal
 * probability (~33%) each color like this:
 *
 * ```gleam
 * let color = random.uniform(Red, [Green, Blue])
 * ```
 */
export function uniform(first, others) {
  return weighted(
    [1.0, first],
    $list.map(others, (value) => { return [1.0, value]; }),
  );
}

/**
 * This function works exactly like `uniform` but will return an `Error(Nil)`
 * if the provided argument is an empty list since the generator wouldn't be
 * able to produce any value in that case.
 *
 * It generates values from the given list with equal probability.
 *
 * ## Examples
 *
 * ```gleam
 * random.try_uniform([])
 * // -> Error(Nil)
 * ```
 *
 * For example if you consider the following type definition to model color:
 *
 * ```gleam
 * type Color {
 *   Red
 *   Green
 *   Blue
 * }
 * ```
 *
 * This call of `try_uniform` will produce a generator wrapped in an `Ok`:
 *
 * ```gleam
 * let assert Ok(color_1) = random.try_uniform([Red, Green, Blue])
 * let color_2 = random.uniform(Red, [Green, Blue])
 * ```
 *
 * The generators `color_1` and `color_2` will behave exactly the same.
 */
export function try_uniform(options) {
  if (options instanceof $Empty) {
    return new Error(undefined);
  } else {
    let first = options.head;
    let rest = options.tail;
    return new Ok(uniform(first, rest));
  }
}

/**
 * This function works exactly like `weighted` but will return an `Error(Nil)`
 * if the provided argument is an empty list since the generator wouldn't be
 * able to produce any value in that case.
 *
 * It generates values from the given list with a weighted probability.
 *
 * ## Examples
 *
 * ```gleam
 * random.try_weighted([])
 * // -> Error(Nil)
 * ```
 *
 * For example if you consider the following type definition to model color:
 *
 * ```gleam
 * type CoinFlip {
 *   Heads
 *   Tails
 * }
 * ```
 *
 * This call of `try_weighted` will produce a generator wrapped in an `Ok`:
 *
 * ```gleam
 * let assert Ok(coin_1) =
 *   random.try_weighted([#(0.75, Heads), #(0.25, Tails)])
 * let coin_2 = random.uniform(#(0.75, Heads), [#(0.25, Tails)])
 * ```
 *
 * The generators `coin_1` and `coin_2` will behave exactly the same.
 */
export function try_weighted(options) {
  if (options instanceof $Empty) {
    return new Error(undefined);
  } else {
    let first = options.head;
    let rest = options.tail;
    return new Ok(weighted(first, rest));
  }
}

/**
 * Generates two values with equal probability.
 *
 * This is a shorthand for `random.uniform(one, [other])`, but can read better
 * when there's only two choices.
 *
 * ## Examples
 *
 * Given the following type to model the outcome of a coin flip:
 *
 * ```gleam
 * pub type CoinFlip {
 *   Heads
 *   Tails
 * }
 * ```
 *
 * You can write a generator for coin flip outcomes like this:
 *
 * ```gleam
 * let flip = random.choose(Heads, Tails)
 * ```
 */
export function choose(one, other) {
  return uniform(one, toList([other]));
}

/**
 * Generates a `Map(k, v)` where each key value pair is generated using the
 * provided generators.
 *
 * This is similar to `fixed_size_dict` with the difference that the map is
 * going to have a random number of key-value pairs between 0 (inclusive) and
 * 32 (inclusive).
 */
export function dict(keys, values) {
  return then$(
    int(0, 32),
    (size) => { return fixed_size_dict(keys, values, size); },
  );
}

function do_fixed_size_dict(
  keys,
  values,
  size,
  unique_keys,
  consecutive_attempts,
  acc
) {
  let has_required_size = unique_keys === size;
  return $bool.guard(
    has_required_size,
    constant(acc),
    () => {
      let has_reached_maximum_attempts = consecutive_attempts >= 10;
      return $bool.guard(
        has_reached_maximum_attempts,
        constant(acc),
        () => {
          return then$(
            keys,
            (key) => {
              let $ = $dict.has_key(acc, key);
              if ($) {
                return do_fixed_size_dict(
                  keys,
                  values,
                  size,
                  unique_keys,
                  consecutive_attempts + 1,
                  acc,
                );
              } else {
                return then$(
                  values,
                  (value) => {
                    let unique_keys$1 = unique_keys + 1;
                    let acc$1 = $dict.insert(acc, key, value);
                    return do_fixed_size_dict(
                      keys,
                      values,
                      size,
                      unique_keys$1,
                      0,
                      acc$1,
                    );
                  },
                );
              }
            },
          );
        },
      );
    },
  );
}

/**
 * Generates a `Dict(k, v)` where each key value pair is generated using the
 * provided generators.
 *
 * > ⚠️ This function makes a best effort at generating a map with exactly the
 * > specified number of keys, but beware that it may contain less items if
 * > the keys generator cannot generate enough distinct keys.
 */
export function fixed_size_dict(keys, values, size) {
  let _pipe = $int.max(size, 0);
  return ((_capture) => {
    return do_fixed_size_dict(keys, values, _capture, 0, 0, $dict.new$());
  })(_pipe);
}

/**
 * Generates a `Set(a)` where each item is generated using the provided
 * generator.
 *
 * This is similar to `fixed_size_set` with the difference that the set is
 * going to have a random size between 0 (inclusive) and 32 (inclusive).
 */
export function set(generator) {
  return then$(
    int(0, 32),
    (size) => { return fixed_size_set(generator, size); },
  );
}

function do_fixed_size_set(
  generator,
  size,
  unique_items,
  consecutive_attempts,
  acc
) {
  let has_required_size = unique_items === size;
  return $bool.guard(
    has_required_size,
    constant(acc),
    () => {
      let has_reached_maximum_attempts = consecutive_attempts >= 10;
      return $bool.guard(
        has_reached_maximum_attempts,
        constant(acc),
        () => {
          return then$(
            generator,
            (item) => {
              let $ = $set.contains(acc, item);
              if ($) {
                return do_fixed_size_set(
                  generator,
                  size,
                  unique_items,
                  consecutive_attempts + 1,
                  acc,
                );
              } else {
                let unique_items$1 = unique_items + 1;
                let acc$1 = $set.insert(acc, item);
                return do_fixed_size_set(
                  generator,
                  size,
                  unique_items$1,
                  0,
                  acc$1,
                );
              }
            },
          );
        },
      );
    },
  );
}

/**
 * Generates a `Set(a)` where each item is generated using the provided
 * generator.
 *
 * > ⚠️ This function makes a best effort at generating a set with exactly the
 * > specified number of items, but beware that it may contain less items if
 * > the given generator cannot generate enough distinct values.
 */
export function fixed_size_set(generator, size) {
  return do_fixed_size_set(generator, $int.max(size, 0), 0, 0, $set.new$());
}

/**
 * I'm not exposing this function because, if one is not careful with the range,
 * it might lead to a nasty infinite loop.
 * When I come up with a better alternative I might make a similar API public,
 * for now, if someone wants to do something unsafe they will have to
 * manually reimplement it.
 * 
 * @ignore
 */
function utf_codepoint_in_range(lower, upper) {
  return then$(
    int(lower, upper),
    (raw_codepoint) => {
      let $ = $string.utf_codepoint(raw_codepoint);
      if ($ instanceof Ok) {
        let codepoint = $[0];
        return constant(codepoint);
      } else {
        return utf_codepoint_in_range(lower, upper);
      }
    },
  );
}

/**
 * Generates Strings with the given number number of UTF code points.
 *
 * > ⚠️ The generated codepoints will be in the range from 0 (inclusive) to
 * > 1023 (inclusive). If you feel like these strings are not enough for your
 * > needs, please open an issue! I'd love to hear your use case and improve
 * > the package.
 */
export function fixed_size_string(size) {
  let _pipe = fixed_size_list(utf_codepoint_in_range(0, 1023), size);
  return map(_pipe, $string.from_utf_codepoints);
}

/**
 * Generates Strings with a random number of UTF code points, between
 * 0 (included) and 32 (included).
 *
 * This is similar to `fixed_size_string`, with the difference that the
 * size is randomly generated as well.
 */
export function string() {
  return then$(int(0, 32), (size) => { return fixed_size_string(size); });
}

/**
 * Generates `BitArray`s with a random size.
 */
export function bit_array() {
  return map(string(), $bit_array.from_string);
}

function do_shuffle(list, acc) {
  let slightly_less_than_1 = 1.0 - min_positive;
  if (list instanceof $Empty) {
    let _pipe = $list.sort(
      acc,
      (one, other) => { return $float.compare(one[0], other[0]); },
    );
    let _pipe$1 = $list.map(_pipe, (pair) => { return pair[1]; });
    return constant(_pipe$1);
  } else {
    let first = list.head;
    let rest = list.tail;
    return then$(
      float(0.0, slightly_less_than_1),
      (order) => { return do_shuffle(rest, listPrepend([order, first], acc)); },
    );
  }
}

/**
 * Generates lists with the same element of the given one, but in a random
 * order.
 */
export function shuffle(list) {
  return do_shuffle(list, toList([]));
}

function log_random() {
  let slightly_less_than_1 = 1.0 - min_positive;
  return then$(
    float(0.0, slightly_less_than_1),
    (float) => {
      let $ = $float.logarithm(float + min_positive);
      let random;
      if ($ instanceof Ok) {
        random = $[0];
      } else {
        throw makeError(
          "let_assert",
          FILEPATH,
          "prng/random",
          705,
          "log_random",
          "Pattern match failed, no pattern matched the value.",
          {
            value: $,
            start: 20857,
            end: 20919,
            pattern_start: 20868,
            pattern_end: 20878
          }
        )
      }
      return constant(random);
    },
  );
}

function sample_loop(list, reservoir, n, w) {
  let $ = $float.logarithm(1.0 - w);
  let log;
  if ($ instanceof Ok) {
    log = $[0];
  } else {
    throw makeError(
      "let_assert",
      FILEPATH,
      "prng/random",
      684,
      "sample_loop",
      "Pattern match failed, no pattern matched the value.",
      {
        value: $,
        start: 20169,
        end: 20215,
        pattern_start: 20180,
        pattern_end: 20187
      }
    )
  }
  return then$(
    log_random(),
    (log_randon) => {
      let skip = $float.round($float.floor(divideFloat(log_randon, log)));
      let $1 = $list.drop(list, skip);
      if ($1 instanceof $Empty) {
        return constant(reservoir);
      } else {
        let first = $1.head;
        let rest = $1.tail;
        return then$(
          int(0, n - 1),
          (position) => {
            return then$(
              log_random(),
              (log_random) => {
                let reservoir$1 = $dict.insert(reservoir, position, first);
                let w$1 = w * $float.exponential(
                  divideFloat(log_random, $int.to_float(n)),
                );
                return sample_loop(rest, reservoir$1, n, w$1);
              },
            );
          },
        );
      }
    },
  );
}

function build_reservoir_loop(loop$list, loop$size, loop$reservoir) {
  while (true) {
    let list = loop$list;
    let size = loop$size;
    let reservoir = loop$reservoir;
    let reservoir_size = $dict.size(reservoir);
    let $ = reservoir_size >= size;
    if ($) {
      return [reservoir, list];
    } else {
      if (list instanceof $Empty) {
        return [reservoir, toList([])];
      } else {
        let first = list.head;
        let rest = list.tail;
        let reservoir$1 = $dict.insert(reservoir, reservoir_size, first);
        loop$list = rest;
        loop$size = size;
        loop$reservoir = reservoir$1;
      }
    }
  }
}

/**
 * Builds the initial reservoir used by Algorithm L.
 * This is a dictionary with keys ranging from `0` up to `n - 1` where each
 * value is the corresponding element at that position in `list`.
 *
 * This also returns the remaining elements of `list` that didn't end up in
 * the reservoir.
 * 
 * @ignore
 */
function build_reservoir(list, n) {
  return build_reservoir_loop(list, n, $dict.new$());
}

/**
 * Generates random samples of up to n elements from a list using reservoir
 * sampling via [Algorithm L](https://en.wikipedia.org/wiki/Reservoir_sampling#Optimal:_Algorithm_L).
 * Returns an empty list if the sample size is less than or equal to 0.
 *
 * Order is not random, only selection is.
 *
 * ## Examples
 *
 * ```gleam
 * sample([1, 2, 3, 4, 5], 3)
 * // some samples could be: [2, 4, 5], [1, 4, 5], ...
 * ```
 */
export function sample(list, n) {
  let $ = build_reservoir(list, n);
  let reservoir = $[0];
  let rest = $[1];
  let $1 = $dict.is_empty(reservoir);
  if ($1) {
    return constant(toList([]));
  } else {
    return then$(
      log_random(),
      (log_random) => {
        let w = $float.exponential(divideFloat(log_random, $int.to_float(n)));
        let _pipe = sample_loop(rest, reservoir, n, w);
        return map(_pipe, $dict.values);
      },
    );
  }
}
